import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

interface CategoryGridProps {
  categories: Array<{
    name: string;
    count: number;
    image: string;
  }>;
  onCategoryClick: (category: string) => void;
}

function getCategoryDescription(category: string): string {
  const descriptions: Record<string, string> = {
    'machines_industrielles': 'Boostez votre production avec des machines performantes et fiables',
    'vehicules': 'Votre flotte professionnelle, financée en toute simplicité',
    'informatique': 'Équipez-vous des dernières technologies pour rester compétitif',
    'bureautique': 'Solutions bureautiques complètes pour votre entreprise',
    'Mobilier_accessoirs_workspace': 'Créez des espaces de travail inspirants et productifs',
    'equipements_professionnels': 'Trouvez le kit parfait adapté à votre métier',
    'agriculture': 'Modernisez votre exploitation avec du matériel agricole de pointe',
    'energie_eau': 'Solutions durables pour votre autonomie énergétique et en eau',
    'evenementiel': 'Tout l\'équipement pour réussir vos événements professionnels',
  };
  
  return descriptions[category] || 'Découvrez notre sélection de produits professionnels';
}

function formatCategoryName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function CategoryGrid({ categories, onCategoryClick }: CategoryGridProps) {
  return (
    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
      {categories.map((category, index) => (
        <motion.div
          key={category.name}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          onClick={() => onCategoryClick(category.name)}
          className="group relative cursor-pointer overflow-hidden rounded-xl bg-white shadow-md hover:shadow-2xl transition-all duration-300 flex flex-col"
        >
          <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative">
            <img
              src={category.image}
              alt={category.name}
              className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary transition-colors mb-2">
                {formatCategoryName(category.name)}
              </h3>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                {getCategoryDescription(category.name)}
              </p>
              <p className="text-sm text-gray-600 font-medium">
                {category.count} {category.count > 1 ? 'produits' : 'produit'}
              </p>
            </div>
            <div className="flex justify-end mt-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 group-hover:bg-primary flex items-center justify-center transition-all duration-300">
                <ChevronRight className="h-6 w-6 text-primary group-hover:text-white group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
